import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { Device } from '@capacitor/device';
import { CoreAnalytics } from '@core-modules/analytics/providers/core-analytics';
import { ClientDetailsProvider } from '@core-modules/client-details/providers/client-details';
import { ItemLimitPipe } from '@core-modules/pipes/item-limit/item-limit';
import { CoreDialogController } from '@core-modules/providers/core-dialogs/core-dialog-controller';
import { CoreHttpClient } from '@core-modules/providers/core-http-client/core-http-client';
import { DeviceProvider } from '@core-modules/providers/device/device';
import { deviceProviderFactory } from '@core-modules/providers/device/device.factory';
import { EnvironmentProvider } from '@core-modules/providers/environment-variables/environment';
import { SessionStorageProvider } from '@core-modules/providers/session-storage/session-storage';
import { SessionStorageMode } from '@core-modules/providers/session-storage/session-storage.mode';
import
    {
        Config,
        IonicModule,
        IonicRouteStrategy,
        Platform
    } from '@ionic/angular';
import { AdvisorsProvider } from 'advisors/providers/advisors-provider';
import { AccountCreateEntryPageGuard } from 'authorization/pages/azureb2c-create-account-entry/azureb2c-create-account-entry.routing.guard';
import { LoginEntryPageGuard } from 'authorization/pages/azureb2c-login-entry/azureb2c-login-entry.routing.guard';
import { PinLoginPageGuard } from 'authorization/pages/pin-login/pin-login.router.guard';
import { AuthProvider } from 'authorization/providers/auth/auth-provider';
import { CoreErrorHandler } from 'errors/providers/error-handler/error-handler';
import { NewsReadPageGuard } from 'news/pages/news-read/news-read.routing.guard';
import { NewsProvider } from 'news/providers/news-provider';
import { IntercomSupportProvider } from 'user-settings/providers/intercom-support';
import { UserSettingsProvider } from 'user-settings/providers/user-settings';
import { PolicyViewPageGuard } from 'valuations/pages/policy-view/policy-view.routing.guard';
import { ValuationsProvider } from 'valuations/providers/valuations-provider';
import { UnauthGuard } from '../authorization/guards/unauth-guard';
import { MessagePageController } from '../messages/providers/message-page-controller';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreApplication } from './providers/core-application';
import { CoreConfiguration } from './providers/core-configuration';
import { CoreHttpClientMode } from '@core-modules/providers/core-http-client/core-http-client.mode';

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        IonicModule.forRoot(),
        AppRoutingModule,
        BrowserAnimationsModule,
        HttpClientModule,
        FormsModule,
        CommonModule
    ],
    providers: [
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        AuthProvider,
        AdvisorsProvider,
        CoreAnalytics,
        CoreApplication,
        CoreConfiguration,
        CoreDialogController,
        CoreErrorHandler,
        CoreHttpClient,
        SessionStorageProvider,
        ClientDetailsProvider,
        EnvironmentProvider,
        NewsProvider,
        ValuationsProvider,
        IntercomSupportProvider,
        UserSettingsProvider,
        ItemLimitPipe,
        // start guards
        UnauthGuard,
        PinLoginPageGuard,
        NewsReadPageGuard,
        PolicyViewPageGuard,
        AccountCreateEntryPageGuard,
        LoginEntryPageGuard,
        // end guards
        MessagePageController,
        { provide: ErrorHandler, useExisting: CoreErrorHandler },
        { provide: DeviceProvider, useFactory: deviceProviderFactory, deps: [Platform] },
        { provide: APP_INITIALIZER, useFactory: AppModule.intializeDeviceInfo, deps: [DeviceProvider], multi: true },
        { provide: APP_INITIALIZER, useFactory: AppModule.intializeHttpClientEngine, deps: [CoreHttpClient, HttpClient], multi: true },
        { provide: APP_INITIALIZER, useFactory: AppModule.intializeSessionStorageEngine, deps: [SessionStorageProvider], multi: true },
        { provide: APP_INITIALIZER, useFactory: AppModule.intializeCoreConfiguration, deps: [CoreConfiguration], multi: true },
        { provide: APP_INITIALIZER, useFactory: AppModule.intializeFirebaseAnalytics, deps: [CoreAnalytics], multi: true },
    ],
    bootstrap: [AppComponent]
})
export class AppModule
{
    //#region Constructor

    /**
     * Class Constructor
     * @param config 
     */
    constructor(public config: Config)
    {

    }

    //#endregion


    //#region Methods

    /**
     * Initialisz the device information
     * @param device 
     */
    public static intializeDeviceInfo(device: DeviceProvider)
    {
        return () => device.loadDeviceInfo();
    }

    /**
    * Initialize the Core HTTP client engine
    * @param coreHttp 
    * @param httpClient 
    */
    public static intializeHttpClientEngine(coreHttp: CoreHttpClient, httpClient: HttpClient)
    {
        return async () =>
        {
            // specify the placeholder for the mode
            let mode: CoreHttpClientMode = null;

            if ((<string>(await Device.getInfo()).platform.toLowerCase()) === 'web')
            {
                // if the app is running on android or ios set the type as application
                mode = CoreHttpClientMode.BROWSER;
            } else
            {
                // else the application is a browser
                mode = CoreHttpClientMode.NATIVE;
            }

            return coreHttp.initializeProvider(mode, httpClient);
        }
    }

    /**
    * Initialize the Session storage provider
    * @param sessionStorageProvider 
    */
    public static intializeSessionStorageEngine(sessionStorageProvider: SessionStorageProvider)
    {
        return async () =>
        {
            // specify the placeholder for the mode
            let mode: SessionStorageMode = null;

            if ((<string>(await Device.getInfo()).platform.toLowerCase()) === 'web')
            {
                // if the app is running on android or ios set the type as application
                mode = SessionStorageMode.BROWSER;
            } else
            {
                // else the application is a browser
                mode = SessionStorageMode.NATIVE;
            }

            return sessionStorageProvider.initializeProvider(mode);
        }
    }

    /**
     * Initialize core Configuration
     * @param coreConfiguration 
     */
    public static intializeCoreConfiguration(coreConfiguration: CoreConfiguration)
    {
        return () =>
        {
            return coreConfiguration.initializeConfiguration();
        }
    }

    /**
     * Initialize Intercom core support
     * @param analytics
     */
    public static intializeFirebaseAnalytics(analytics: CoreAnalytics)
    {
        return async () =>
        {
            let platform = (await Device.getInfo()).platform.toLowerCase();

            return analytics.InitializeAnalyticsService(platform);
        }
    }
    //#endregion
}